import {
  LINQ_SMS_BLOCKED_COUNTRIES,
  TWILIO_SMS_BLOCKED_COUNTRIES,
} from "@repo/common";
import type { Country, Value as PhoneValue } from "react-phone-number-input";
import { parsePhoneNumber } from "react-phone-number-input";

// Re-export shared utilities so existing imports keep working
export { friendlyPhoneError } from "@repo/common";

/**
 * The ISO alpha-2 country the tRPC error formatter forwards from
 * `ApiError.metadata.country`. Present on PHONE_COUNTRY_NOT_SUPPORTED, whose
 * copy names the country; the code itself stays constant so one blocked country
 * does not mint its own error string. tRPC's inferred error shape does not
 * surface the field, so pluck it via a permissive cast (same as `violation`).
 */
export function readErrorCountry(error: {
  data?: unknown;
}): string | undefined {
  const country = (error.data as { country?: unknown } | null | undefined)
    ?.country;
  return typeof country === "string" ? country : undefined;
}

/**
 * CAPABILITY ONLY; these helpers answer "can this vendor deliver an SMS
 * here", never "is Terac available here". Policy lives in the
 * `platform-access-blocked` PostHog flag and reaches the client through
 * `useCountryAvailability`. Conflating the two is what made Twilio-blocked
 * countries look un-launched when Twilio simply could not text them, and what
 * hid Nigeria behind the Whop payout allowlist.
 */

/** Twilio geo-permissions allow an OTP to this country. */
export function canTwilioReachCountry(country: Country | undefined): boolean {
  if (!country) {
    return true;
  }
  return !TWILIO_SMS_BLOCKED_COUNTRIES.has(country);
}

/**
 * Twilio geo-permissions allow an OTP to this number. Undefined or
 * unparseable country: let the backend decide.
 */
export function canTwilioReachPhone(phone: PhoneValue | undefined): boolean {
  if (!phone) {
    return true;
  }
  try {
    return canTwilioReachCountry(parsePhoneNumber(phone)?.country);
  } catch {
    return true;
  }
}

/**
 * Linq can deliver the inbound verification token to this country.
 * Deliberately independent of the Twilio geo-block, which only applies to the
 * Twilio OTP fallback.
 */
export function canLinqReachCountry(country: Country | undefined): boolean {
  if (!country) {
    return true;
  }
  return !LINQ_SMS_BLOCKED_COUNTRIES.has(country);
}
